import { useState, useCallback, useEffect } from 'react';
import SublyApi from '../../../../helpers/Api';

/**
 * Custom hook for managing expenses with real API integration
 *
 * @param {string|null} username - Username for admin viewing specific user's expenses
 *                                  If null, uses logged-in user's expenses (client mode)
 * @returns {Object} Expense management functions and state
 */
export const useExpenses = (username = null) => {
	const [expenses, setExpenses] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [history, setHistory] = useState([]);

	// Fetch expenses from API
	const fetchExpenses = useCallback(async (filters = {}) => {
		setLoading(true);
		setError(null);

		try {
			let data;

			// Admin mode: fetch expenses for specific user
			if (username) {
				data = await SublyApi.getUserExpenses(username, filters);
			}
			// Client mode: fetch logged-in user's expenses
			else {
				data = await SublyApi.getExpenses(filters);
			}

			// Normalize expense data: convert expenseDate → date for frontend consistency
			const expenses = (data.expenses || data).map(expense => ({
				...expense,
				date: expense.expenseDate || expense.date
			}));

			setExpenses(expenses);
		} catch (err) {
			setError(err.message || 'Failed to load expenses');
			console.error('Error fetching expenses:', err);
		} finally {
			setLoading(false);
		}
	}, [username]);

	// Add new expense
	const addExpense = useCallback(async (expenseData) => {
		setLoading(true);
		setError(null);

		try {
			// Convert frontend camelCase to backend snake_case
			const apiData = {
				name: expenseData.name,
				category: expenseData.category,
				amount: expenseData.amount,
				expense_date: expenseData.date,
				notes: expenseData.notes || ''
			};

			let newExpense;

			// Admin mode: create expense for specific user
			if (username) {
				newExpense = await SublyApi.createExpenseForUser({
					username,
					...apiData
				});
			}
			// Client mode: create expense for logged-in user
			else {
				newExpense = await SublyApi.createExpense(apiData);
			}

			// Normalize and add to state
			const normalizedExpense = {
				...(newExpense.expense || newExpense),
				date: (newExpense.expense || newExpense).expenseDate || (newExpense.expense || newExpense).date
			};

			setExpenses(prev => [...prev, normalizedExpense]);

			return normalizedExpense;
		} catch (err) {
			setError(err.message || 'Failed to create expense');
			throw err;
		} finally {
			setLoading(false);
		}
	}, [username]);

	// Update expense
	const updateExpense = useCallback(async (id, updates) => {
		const oldExpense = expenses.find(e => e.id === id);

		// Optimistic update
		setExpenses(prev =>
			prev.map(expense =>
				expense.id === id ? { ...expense, ...updates } : expense
			)
		);

		try {
			// Convert to snake_case for API
			const apiUpdates = {};
			if (updates.date) apiUpdates.expense_date = updates.date;
			if (updates.name) apiUpdates.name = updates.name;
			if (updates.category) apiUpdates.category = updates.category;
			if (updates.amount !== undefined) apiUpdates.amount = updates.amount;
			if (updates.notes !== undefined) apiUpdates.notes = updates.notes;

			let updatedExpense;

			// Admin mode: update any expense
			if (username) {
				updatedExpense = await SublyApi.updateExpenseAsAdmin(id, apiUpdates);
			}
			// Client mode: update user's own expense
			else {
				updatedExpense = await SublyApi.updateExpense(id, apiUpdates);
			}

			// Add to history for undo
			setHistory(h => [...h, { action: 'update', id, old: oldExpense }]);

			// Update with server response
			setExpenses(prev =>
				prev.map(expense =>
					expense.id === id ? (updatedExpense.expense || updatedExpense) : expense
				)
			);
		} catch (err) {
			// Rollback on error
			setExpenses(prev =>
				prev.map(expense =>
					expense.id === id ? oldExpense : expense
				)
			);
			setError(err.message || 'Failed to update expense');
			throw err;
		}
	}, [expenses, username]);

	// Delete expense
	const deleteExpense = useCallback(async (id) => {
		const deletedExpense = expenses.find(e => e.id === id);

		// Optimistic update
		setExpenses(prev => prev.filter(expense => expense.id !== id));

		try {
			// Admin mode: delete any expense
			if (username) {
				await SublyApi.deleteExpenseAsAdmin(id);
			}
			// Client mode: delete user's own expense
			else {
				await SublyApi.deleteExpense(id);
			}

			// Add to history for undo
			setHistory(h => [...h, { action: 'delete', expense: deletedExpense }]);
		} catch (err) {
			// Rollback on error
			setExpenses(prev => [...prev, deletedExpense]);
			setError(err.message || 'Failed to delete expense');
			throw err;
		}
	}, [expenses, username]);

	// Bulk delete expenses
	const bulkDeleteExpenses = useCallback(async (ids) => {
		const deletedExpenses = expenses.filter(e => ids.includes(e.id));

		// Optimistic update
		setExpenses(prev => prev.filter(expense => !ids.includes(expense.id)));

		try {
			// Delete each expense individually
			const deletePromises = ids.map(id =>
				username
					? SublyApi.deleteExpenseAsAdmin(id)
					: SublyApi.deleteExpense(id)
			);

			await Promise.all(deletePromises);

			// Add to history for undo
			setHistory(h => [...h, { action: 'bulkDelete', expenses: deletedExpenses }]);
		} catch (err) {
			// Rollback on error
			setExpenses(prev => [...prev, ...deletedExpenses]);
			setError(err.message || 'Failed to delete expenses');
			throw err;
		}
	}, [expenses, username]);

	// Undo last action (note: this won't make API calls to reverse)
	const undo = useCallback(() => {
		if (history.length === 0) return;

		const lastAction = history[history.length - 1];

		if (lastAction.action === 'delete') {
			setExpenses(prev => [...prev, lastAction.expense]);
		} else if (lastAction.action === 'update') {
			setExpenses(prev =>
				prev.map(e => (e.id === lastAction.id ? lastAction.old : e))
			);
		} else if (lastAction.action === 'bulkDelete') {
			setExpenses(prev => [...prev, ...lastAction.expenses]);
		}

		setHistory(h => h.slice(0, -1));
	}, [history]);

	// Helper function for filtering expenses by month
	const getExpensesByMonth = useCallback((month) => {
		return expenses.filter(expense => {
			// month is in format YYYY-MM
			// expenseDate is ISO string like "2025-10-15T07:00:00.000Z"
			const expenseMonth = expense.expenseDate?.substring(0, 7) || expense.date?.substring(0, 7);
			return expenseMonth === month;
		});
	}, [expenses]);

	return {
		expenses,
		loading,
		error,
		fetchExpenses,
		addExpense,
		updateExpense,
		deleteExpense,
		bulkDeleteExpenses,
		undo,
		canUndo: history.length > 0,
		getExpensesByMonth, // Helper for month filtering
	};
};
